import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Star, 
  Clock, 
  Home, 
  MapPin, 
  BedDouble, 
  Bath, 
  Maximize, 
  Car, 
  Check, 
  ChevronRight, 
  Calendar, 
  Heart, 
  Share2 
} from 'lucide-react';

// Sample data for testing
const premiumProperties = [
    {
      id: 1,
      title: "Malabar Hill Residences",
      type: "Luxury Villa",
      location: "Shimla, Himachal Pradesh",
      price: "₹4.8 Cr",
      bedrooms: 5,
      bathrooms: 6,
      area: "7,200 sq.ft.",
      parking: 3,
      featured: true,
      new: true,
      status: "Ready to Move",
      amenities: ["Private Pool", "Garden", "Home Theater", "Smart Home", "Terrace", "Mountain View"],
      description: "Perched on the prestigious hillside of Shimla, this magnificent villa offers panoramic views of the Himalayan range. Built with premium materials and architectural excellence, the property features spacious living areas, a private garden, and cutting-edge smart home technology.",
      images: [
        "https://ik.imagekit.io/7mw0on9oya/h1.jpg?updatedAt=1746097191244"
      ]
    },
    {
      id: 2,
      title: "Evergreen Heights",
      type: "Luxury Apartment",
      location: "Manali, Himachal Pradesh",
      price: "₹2.9 Cr",
      bedrooms: 4,
      bathrooms: 4,
      area: "3,850 sq.ft.",
      parking: 2,
      featured: true,
      new: false,
      status: "Under Construction",
      amenities: ["Spa", "Fitness Center", "Concierge", "Rooftop Deck", "24/7 Security", "Valley View"],
      description: "Experience the pinnacle of mountain luxury living in these meticulously designed apartments. Each residence boasts premium finishes, floor-to-ceiling windows that frame breathtaking views, and access to world-class amenities including a spa and fitness center.",
      images: [
        "https://ik.imagekit.io/7mw0on9oya/h3.jpg?updatedAt=1746097191215",
      
      ]
    },
    {
      id: 3,
      title: "Cedar Sanctuary",
      type: "Countryside Estate",
      location: "Kasauli, Himachal Pradesh",
      price: "₹6.5 Cr",
      bedrooms: 6,
      bathrooms: 7,
      area: "9,500 sq.ft.",
      parking: 4,
      featured: true,
      new: true,
      status: "Ready to Move",
      amenities: ["Private Orchard", "Wine Cellar", "Guest House", "Infinity Pool", "Helipad", "Forest View"],
      description: "An extraordinary countryside estate nestled among ancient cedar trees. This magnificent property spans over 2 acres of private land, offering unparalleled privacy and natural beauty. The main residence features exquisite architectural details, while the grounds include a guest house and private orchard.",
      images: [
        "https://ik.imagekit.io/7mw0on9oya/h2.jpg?updatedAt=1746097191308",

      ]
    },
    {
      id: 4,
      title: "Pinewood Manor",
      type: "Heritage Villa",
      location: "Dharamshala, Himachal Pradesh",
      price: "₹3.7 Cr",
      bedrooms: 4,
      bathrooms: 4,
      area: "5,800 sq.ft.",
      parking: 2,
      featured: false,
      new: false,
      status: "Ready to Move",
      amenities: ["Fireplace", "Library", "Meditation Room", "Organic Garden", "Mountain View", "Solar Powered"],
      description: "This beautifully restored heritage villa combines historic charm with modern luxury. Set against the backdrop of snow-capped mountains, the property offers a tranquil living environment with sustainable features including solar power and an organic garden.",
      images: [
        "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=2670&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1513584684374-8bab748fbf90?q=80&w=2670&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=2670&auto=format&fit=crop"
      ]
    },
    {
      id: 5,
      title: "Riverside Retreat",
      type: "Luxury Cottage",
      location: "Kullu, Himachal Pradesh",
      price: "₹2.1 Cr",
      bedrooms: 3,
      bathrooms: 3,
      area: "2,900 sq.ft.",
      parking: 2,
      featured: false,
      new: true,
      status: "Ready to Move",
      amenities: ["River Access", "Jacuzzi", "Outdoor Kitchen", "Deck", "Firepit", "Private Trail"],
      description: "A serene waterfront property offering the perfect blend of luxury and nature. This cottage features floor-to-ceiling windows that showcase the river views, a private deck, and direct access to the water. The tastefully designed interior combines rustic elements with high-end finishes.",
      images: [
        "https://images.unsplash.com/photo-1605146769289-440113cc3d00?q=80&w=2670&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1600607688969-a5bfcd646154?q=80&w=2670&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2670&auto=format&fit=crop"
      ]
    },
    {
      id: 6,
      title: "blue Valley Penthouse",
      type: "Luxury Penthouse",
      location: "Solan, Himachal Pradesh",
      price: "₹3.2 Cr",
      bedrooms: 3,
      bathrooms: 4,
      area: "4,100 sq.ft.",
      parking: 2,
      featured: false,
      new: false,
      status: "Ready to Move",
      amenities: ["Private Elevator", "Terrace Garden", "Home Office", "Wine Cellar", "Entertainment Lounge", "Panoramic Views"],
      description: "An exclusive penthouse offering unmatched luxury and privacy. This residence spans the entire top floor, providing 360-degree views of the valley. The interior features bespoke finishes, designer fixtures, and an open floor plan that seamlessly connects indoor and outdoor living spaces.",
      images: [
        "https://images.unsplash.com/photo-1572120360610-d971b9d7767c?q=80&w=2670&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?q=80&w=2670&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?q=80&w=2670&auto=format&fit=crop"
      ]
    }
  ];

// Main Component
const LuxuryEstateApp = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Featured Properties Section */}
      <FeaturedProperties properties={premiumProperties.filter(p => p.featured)} />
    </div>
  );
};

// Featured Properties Section
const FeaturedProperties = ({ properties }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1
  });
  
  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);
  
  // Make sure we have properties before trying to access them
  if (!properties || properties.length === 0) {
    return null;  // Return null or a fallback UI
  }
  
  // Handle boundary case when currentIndex might be out of bounds
  const safeIndex = Math.min(currentIndex, properties.length - 1);
  const property = properties[safeIndex];
  
  const handleImageClick = (idx) => {
    setCurrentImageIndex(idx);
  };
  
  return (
    <section ref={ref} className="pb-12 bg-white dark:bg-gray-800 relative" id="properties">
      {/* Decorative Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-2/3 h-2/3 bg-emerald-50 dark:bg-emerald-900/20 rounded-bl-[100px] z-0"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-amber-50 dark:bg-amber-900/20 rounded-tr-[100px] z-0"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10 pt-16">
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={controls}
            variants={{
              visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
              hidden: { opacity: 0, y: 20 }
            }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 rounded-full font-medium text-sm mb-4">
              <Star size={16} className="mr-2" />
              <span>Featured Offerings</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Our <span className="text-blue-600 dark:text-blue-400">Signature</span> Properties
            </h2>
            
            <div className="w-24 h-1 bg-blue-600 dark:bg-blue-500 mx-auto mb-6"></div>
            
            <p className="text-gray-600 dark:text-gray-300 max-w-3xl mx-auto text-lg">
            Discover our most prestigious properties, each offering the perfect blend of location, architecture, and luxury amenities for discerning homeowners.
            </p>
          </motion.div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Property Image Carousel */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={controls}
            variants={{
              visible: { opacity: 1, x: 0, transition: { duration: 0.8, delay: 0.3 } },
              hidden: { opacity: 0, x: -50 }
            }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl dark:shadow-blue-900/20">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`image-${currentImageIndex}-property-${safeIndex}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="aspect-w-4 aspect-h-3"
                >
                  <img 
                    src={property.images[currentImageIndex]} 
                    alt={`${property.title} - View ${currentImageIndex + 1}`} 
                    className="w-[95%] h-full object-cover sm:w-full"
                    // in mobile width 95%

                  />
                </motion.div>
              </AnimatePresence>
              
              {/* Status Tag */}
              <div className="absolute top-6 left-6 z-10">
                <div className="bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm shadow-lg flex items-center">
                  <Clock size={16} className="mr-2" />
                  <span>{property.status}</span>
                </div>
              </div>
              
              {/* New Tag */}
              {property.new && (
                <div className="absolute top-6 right-6 z-10">
                  <div className="bg-amber-500 dark:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium text-sm shadow-lg">
                    New Launch
                  </div>
                </div>
              )}
              
              {/* Price Tag */}
              <div className="absolute bottom-14 right-6 z-10">
                <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm text-gray-900 dark:text-gray-100 px-3 py-1 rounded-lg font-bold text-xl shadow-lg">
                  {property.price}
                </div>
              </div>
              
              {/* Thumbnail Preview */}
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 z-20">
                <div className="flex gap-3 bg-white dark:bg-gray-800 p-2 rounded-xl shadow-xl">
                  {property.images.map((image, idx) => (
                    <div
                      key={idx}
                      className={`w-16 h-16 rounded-lg overflow-hidden cursor-pointer transition-all ${idx === currentImageIndex ? 'ring-2 ring-blue-500 dark:ring-blue-400' : 'opacity-70 hover:opacity-100'}`}
                      onClick={() => handleImageClick(idx)}
                    >
                      <img src={image} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* 3D Effect Corner */}
            <div
  className="
    absolute
    -top-5
    -left-2        
    lg:-left-4    
    w-24 h-24
    border-t-4 border-l-4
    border-blue-500 dark:border-blue-600
    rounded-tl-2xl
  "
></div>

{/* Bottom-right corner */}
<div
  className="
    absolute
    -bottom-5
    -right-1        
    lg:-right-4     
    w-24 h-24
    border-b-4 border-r-4
    border-amber-500 dark:border-amber-600
    rounded-br-2xl
  "
></div>
          </motion.div>
          
          {/* Property Details */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={controls}
            variants={{
              visible: { opacity: 1, x: 0, transition: { duration: 0.8, delay: 0.5 } },
              hidden: { opacity: 0, x: -50 }
            }}
          >
            <div className="space-y-8">
              <div>
                <div className="inline-flex items-center bg-amber-100/60 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 px-3 py-1 rounded-full text-sm font-medium mb-4">
                  <Home size={14} className="mr-1" />
                  <span>{property.type}</span>
                </div>
                
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-3">
                  {property.title}
                </h3>
                
                <div className="flex items-center text-gray-600 dark:text-gray-400 mb-6">
                  <MapPin size={18} className="mr-2 text-blue-600 dark:text-blue-400" />
                  <span>{property.location}</span>
                </div>
                
                <p className="text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
                  {property.description}
                </p>
              </div>
              
              {/* Property Specs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-100 dark:border-gray-600">
                  <BedDouble size={24} className="text-blue-600 dark:text-blue-400 mb-2" />
                  <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{property.bedrooms}</span>
                  <span className="text-gray-500 dark:text-gray-400 text-sm">Bedrooms</span>
                </div>
                
                <div className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-100 dark:border-gray-600">
                  <Bath size={24} className="text-blue-600 dark:text-blue-400 mb-2" />
                  <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{property.bathrooms}</span>
                  <span className="text-gray-500 dark:text-gray-400 text-sm">Bathrooms</span>
                </div>
                
                <div className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-100 dark:border-gray-600">
                  <Maximize size={24} className="text-blue-600 dark:text-blue-400 mb-2" />
                  <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{property.area}</span>
                  <span className="text-gray-500 dark:text-gray-400 text-sm">Living Area</span>
                </div>
                
                <div className="flex flex-col items-center p-4 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-100 dark:border-gray-600">
                  <Car size={24} className="text-blue-600 dark:text-blue-400 mb-2" />
                  <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{property.parking}</span>
                  <span className="text-gray-500 dark:text-gray-400 text-sm">Parking</span>
                </div>
              </div>
              
              {/* Amenities */}
              <div className="space-y-3">
                <h4 className="text-xl font-bold text-gray-900 dark:text-gray-100">Key Amenities</h4>
                <div className="grid grid-cols-2 gap-2">
                  {property.amenities.map((amenity, idx) => (
                    <div key={idx} className="flex items-center">
                      <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center mr-2">
                        <Check size={12} className="text-blue-600 dark:text-blue-400" />
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Navigation Dots */}
        <div className="flex justify-center mt-20 gap-2">
          {properties.map((_, idx) => (
            <button
              key={idx}
              className={`h-3 rounded-full transition-all ${
                idx === safeIndex 
                  ? 'bg-blue-600 dark:bg-blue-500 w-8' 
                  : 'bg-gray-300 dark:bg-gray-600 w-3 hover:bg-gray-400 dark:hover:bg-gray-500'
              }`}
              onClick={() => setCurrentIndex(idx)}
              aria-label={`View property ${idx + 1}`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LuxuryEstateApp;